import {
  BookOpenTextIcon,
  BriefcaseBusinessIcon,
  CompassIcon,
  GraduationCapIcon,
  HeartHandshakeIcon,
  LandmarkIcon,
  StethoscopeIcon,
} from 'lucide-react';
import type { Workspace } from '../types/content';

const DEMO_BASE = 'https://noetivra-workspace.abledream3.chatgpt.site/';

export const workspaces: Workspace[] = [
  {
    slug: 'research',
    name: 'Research',
    icon: BookOpenTextIcon,
    summary:
      'Connect sources, compare studies, review proposed relationships, and build traceable institutional knowledge.',
    audience: 'For research groups, libraries, and institutional knowledge teams',
    headline: 'Build knowledge you can trace back to the source.',
    intro:
      'Researchers connect sources, compare studies, and examine the relationships between them. Curators review what is proposed and govern terminology, so every finding stays linked to the passage that supports it.',
    tasks: [
      { title: 'Connect sources', body: 'Bring studies, reports, and datasets into one library, linked by the concepts they share.' },
      { title: 'Compare studies', body: 'Place findings side by side and see where evidence agrees, differs, or qualifies a claim.' },
      { title: 'Review proposed relationships', body: 'Examine each suggested connection with its supporting passages before it becomes knowledge.' },
      { title: 'Govern terminology and provenance', body: 'Keep terms consistent and every decision traceable to a reviewer, a time, and a version.' },
    ],
    surfaces: [
      {
        name: 'Research Workspace',
        audience: 'For researchers and analysts',
        body: 'Explore the library, follow relationships across disciplines, and propose new connections from the passages you read.',
        items: ['Source library and reader', 'Cross-study comparison', 'Knowledge map exploration', 'Proposals with linked passages'],
      },
      {
        name: 'Curation & Administration',
        audience: 'For curators, librarians, and administrators',
        body: 'Review proposed relationships, manage terminology, and oversee provenance and access across the institution.',
        items: ['Relationship review queue', 'Terminology governance', 'Provenance and version history', 'Roles and access'],
      },
    ],
    workflow: {
      title: 'From a single passage to institutional knowledge',
      steps: [
        { title: 'Open a source', body: 'A researcher opens a sample study on sleep and cognition.' },
        { title: 'Inspect the passage', body: 'The supporting sentence is highlighted in context.' },
        { title: 'Review the relationship', body: 'A curator weighs it against two other sources.' },
        { title: 'Approve the finding', body: 'The decision is recorded with reviewer and version.' },
        { title: 'Explore the map', body: 'The finding appears on the knowledge map, still linked to its evidence.' },
      ],
    },
    modules: ['knowledge'],
    demo: {
      url: `${DEMO_BASE}#research-chooser`,
      name: 'Noetivra Research',
      tryText: 'Open a sample study, inspect a supporting passage, and approve a proposed relationship.',
    },
    preview: {
      surface: 'Curation & Administration',
      title: 'Relationship review',
      filters: ['All', 'Awaiting review', 'Terminology'],
      rows: [
        { title: 'Sleep fragmentation → Working memory', meta: '3 sources · proposed 2h ago', status: 'Awaiting review', tone: 'warn' },
        { title: 'Urban heat → Hospital admissions', meta: '5 sources · yesterday', status: 'Changes requested', tone: 'alert' },
        { title: 'Microplastics → Soil microbiota', meta: '2 sources · Mon', status: 'Approved', tone: 'ok' },
        { title: 'Merge: cognitive load / mental workload', meta: 'Terminology · Mon', status: 'Awaiting review', tone: 'warn' },
      ],
      detail: {
        title: 'Sleep fragmentation → reduced working memory',
        subtitle: 'Proposed relationship · Sample record',
        fields: [
          { label: 'Relation', value: 'associated with' },
          { label: 'Evidence', value: '2 supporting · 1 qualifying' },
          { label: 'Confidence', value: 'Moderate' },
          { label: 'Domain', value: 'Cognitive science' },
        ],
        timeline: [
          { label: 'Proposed from Hendricks et al., 2023', time: '2h' },
          { label: 'Passage linked by R. Lindqvist', time: '1h' },
          { label: 'Assigned to curator review', time: 'now' },
        ],
        primaryAction: 'Approve',
        secondaryAction: 'Request changes',
      },
    },
  },
  {
    slug: 'government',
    name: 'Government',
    icon: LandmarkIcon,
    summary:
      'Coordinate citizen support, manage referrals, review cases, and understand delivery across administrative areas.',
    audience: 'For public institutions, agencies, and the citizens they serve',
    headline: 'Public services, coordinated from first request to follow-up.',
    intro:
      'Citizens find support and follow their requests in plain language. Caseworkers and program leads manage referrals, review cases, and see how services are being delivered across every administrative area.',
    tasks: [
      { title: 'Coordinate citizen support', body: 'Receive requests through one front door and route them to the right service.' },
      { title: 'Manage referrals', body: 'Issue, track, and close referrals between departments and partner providers.' },
      { title: 'Review cases', body: 'See a case’s history, documents, and decisions in one accountable record.' },
      { title: 'Understand delivery by area', body: 'Compare demand, response times, and outcomes across districts and regions.' },
    ],
    surfaces: [
      {
        name: 'Citizen Workspace',
        audience: 'For residents and service users',
        body: 'A clear, personal space to find support, submit requests, and see where each one stands.',
        items: ['Find and request services', 'Track referrals and next steps', 'Development opportunities', 'Personal records and credentials'],
      },
      {
        name: 'Administration Workspace',
        audience: 'For caseworkers, program leads, and administrators',
        body: 'An operational view of requests, referrals, and cases, with delivery insight across administrative areas.',
        items: ['Referral and case queues', 'Case review and decisions', 'Area-level delivery overview', 'Audit-ready activity history'],
      },
    ],
    workflow: {
      title: 'A support request, end to end',
      steps: [
        { title: 'Citizen submits a request', body: 'A resident asks for housing support in the Citizen Workspace.' },
        { title: 'Request is routed', body: 'It reaches the housing team for the resident’s district.' },
        { title: 'Caseworker reviews', body: 'History, documents, and eligibility notes sit in one case.' },
        { title: 'Referral is issued', body: 'The resident sees the referral and the next step immediately.' },
        { title: 'Delivery is reviewed', body: 'Program leads see response times across all areas.' },
      ],
    },
    modules: ['services', 'capability', 'credentials', 'knowledge'],
    demo: {
      url: `${DEMO_BASE}#chooser`,
      name: 'National Capability and Development System',
      tryText: 'Submit a sample request as a citizen, then review and refer it as an administrator.',
    },
    preview: {
      surface: 'Administration Workspace',
      title: 'Referral queue',
      filters: ['All areas', 'North District', 'Follow-up due'],
      rows: [
        { title: 'Housing support · NC-1042', meta: 'North District · received today', status: 'New', tone: 'accent' },
        { title: 'Employment training · NC-1038', meta: 'Riverside · 2 days', status: 'In review', tone: 'warn' },
        { title: 'Disability services · NC-1031', meta: 'Central · 4 days', status: 'Referred', tone: 'ok' },
        { title: 'Childcare assistance · NC-1027', meta: 'East Valley · 6 days', status: 'Follow-up due', tone: 'alert' },
      ],
      detail: {
        title: 'Housing support request',
        subtitle: 'Case NC-1042 · Sample record',
        fields: [
          { label: 'Area', value: 'North District' },
          { label: 'Service', value: 'Housing assistance' },
          { label: 'Priority', value: 'Standard' },
          { label: 'Assigned', value: 'J. Moreno' },
        ],
        timeline: [
          { label: 'Request submitted by resident', time: '9:12' },
          { label: 'Routed to North District housing', time: '9:13' },
          { label: 'Documents verified', time: '10:40' },
        ],
        primaryAction: 'Issue referral',
        secondaryAction: 'Request information',
      },
    },
  },
  {
    slug: 'education',
    name: 'Education',
    icon: GraduationCapIcon,
    summary: 'Connect learning goals, evidence, feedback, and development pathways.',
    audience: 'For schools, universities, and learning programs',
    headline: 'Connect what learners aim for with how they grow.',
    intro:
      'Learners see their goals, the evidence of their progress, and what comes next. Educators give feedback against clear criteria and shape development pathways that carry forward from term to term.',
    tasks: [
      { title: 'Set learning goals', body: 'Define goals that learners and educators share, linked to clear criteria.' },
      { title: 'Gather evidence', body: 'Attach drafts, projects, and observations to the goals they demonstrate.' },
      { title: 'Give feedback', body: 'Respond against criteria, so feedback is specific and comparable over time.' },
      { title: 'Plan development pathways', body: 'Turn feedback into next steps that follow the learner across courses.' },
    ],
    surfaces: [
      {
        name: 'Learner Workspace',
        audience: 'For students and learners',
        body: 'A personal view of goals, submitted evidence, feedback received, and the pathway ahead.',
        items: ['Goals and criteria', 'Evidence portfolio', 'Feedback history', 'Development pathway'],
      },
      {
        name: 'Educator Workspace',
        audience: 'For teachers, faculty, and program coordinators',
        body: 'Review evidence, give structured feedback, and see progress across a class or cohort.',
        items: ['Evidence review queue', 'Criteria-based feedback', 'Cohort progress', 'Pathway planning'],
      },
    ],
    workflow: {
      title: 'From a learning goal to the next step',
      steps: [
        { title: 'Define a goal', body: '“Design a controlled experiment,” with three criteria.' },
        { title: 'Attach evidence', body: 'The learner submits a lab report and data sheet.' },
        { title: 'Give feedback', body: 'The educator responds against each criterion.' },
        { title: 'Update the pathway', body: 'A focused next step is added to the learner’s plan.' },
        { title: 'Reassess progress', body: 'New evidence shows how the learner has developed.' },
      ],
    },
    modules: ['capability', 'credentials', 'knowledge'],
    demo: {
      url: `${DEMO_BASE}#education-chooser`,
      name: 'Noetivra for Education',
      tryText: 'Review a sample lab report against learning criteria and update a development pathway.',
    },
    preview: {
      surface: 'Educator Workspace',
      title: 'Evidence review',
      filters: ['All', 'Feedback due', 'Year 11'],
      rows: [
        { title: 'Lab report: Reaction rates', meta: 'Chemistry · M. Chen', status: 'Feedback due', tone: 'warn' },
        { title: 'Essay draft: Civic participation', meta: 'Social Studies · A. Rahman', status: 'In progress', tone: 'accent' },
        { title: 'Presentation: Renewable grids', meta: 'Physics · L. Novak', status: 'Reviewed', tone: 'ok' },
        { title: 'Portfolio: Data storytelling', meta: 'Mathematics · K. Sato', status: 'Reviewed', tone: 'ok' },
      ],
      detail: {
        title: 'Lab report: Reaction rates',
        subtitle: 'Submitted by M. Chen · Sample record',
        fields: [
          { label: 'Learning goal', value: 'Design a controlled experiment' },
          { label: 'Evidence', value: '2 drafts · 1 data sheet' },
          { label: 'Criteria', value: 'Method · Analysis · Conclusion' },
          { label: 'Pathway', value: 'Scientific inquiry · Stage 3' },
        ],
        timeline: [
          { label: 'First draft submitted', time: 'Mon' },
          { label: 'Peer comments added', time: 'Tue' },
          { label: 'Final draft submitted', time: 'Today' },
        ],
        primaryAction: 'Give feedback',
        secondaryAction: 'View pathway',
      },
    },
  },
  {
    slug: 'talent',
    name: 'Talent & Workforce',
    icon: BriefcaseBusinessIcon,
    summary: 'Review work samples, assess role-relevant capabilities, and support workforce development.',
    audience: 'For employers, HR teams, and workforce programs',
    headline: 'See capability in the work itself.',
    intro:
      'Employers define the capabilities a role really needs, then review work samples against them. Employees and candidates receive clear feedback and a development plan they can act on.',
    tasks: [
      { title: 'Review work samples', body: 'Assess real tasks, from case exercises to code reviews, rather than self-descriptions.' },
      { title: 'Assess role-relevant capabilities', body: 'Use rubrics built around what the role requires, applied consistently.' },
      { title: 'Plan workforce development', body: 'Turn assessment results into development plans for individuals and teams.' },
      { title: 'Track growth over time', body: 'Reassess and record demonstrated capabilities as people progress.' },
    ],
    surfaces: [
      {
        name: 'Employer Workspace',
        audience: 'For hiring managers, HR, and assessors',
        body: 'Define role capabilities, review work samples, and plan development across teams.',
        items: ['Role capability profiles', 'Work sample review', 'Team development plans', 'Workforce overview'],
      },
      {
        name: 'Candidate & Employee Workspace',
        audience: 'For candidates and employees',
        body: 'Complete work samples, receive feedback, and follow a personal development plan.',
        items: ['Work sample tasks', 'Feedback and results', 'Personal development plan', 'Credential records'],
      },
    ],
    workflow: {
      title: 'From a work sample to a development plan',
      steps: [
        { title: 'Define role capabilities', body: 'A data analyst role lists four core capabilities.' },
        { title: 'Submit a work sample', body: 'A candidate completes a sample case exercise.' },
        { title: 'Assess against criteria', body: 'A reviewer scores each capability with evidence.' },
        { title: 'Share feedback and a plan', body: 'Strengths and next steps are shared with the candidate.' },
        { title: 'Reassess and record', body: 'Growth is captured as a demonstrated capability.' },
      ],
    },
    modules: ['capability', 'credentials'],
    demo: {
      url: `${DEMO_BASE}#talent-chooser`,
      name: 'Noetivra Talent',
      tryText: 'Review a sample work exercise against a role rubric and draft a development plan.',
    },
    preview: {
      surface: 'Employer Workspace',
      title: 'Work sample review',
      filters: ['All roles', 'Ready for review', 'Data Analyst II'],
      rows: [
        { title: 'Case exercise · Data Analyst II', meta: 'Sample candidate S. Ortega', status: 'Ready for review', tone: 'warn' },
        { title: 'Code review · Platform Engineer', meta: 'Sample candidate D. Mensah', status: 'In review', tone: 'accent' },
        { title: 'Client brief · Account Lead', meta: 'Employee R. Iyer', status: 'Plan shared', tone: 'ok' },
        { title: 'Incident write-up · Support Lead', meta: 'Employee T. Brandt', status: 'Reassess due', tone: 'alert' },
      ],
      detail: {
        title: 'Case exercise · Data Analyst II',
        subtitle: 'S. Ortega · Sample record',
        fields: [
          { label: 'Capabilities', value: 'Data reasoning · Communication' },
          { label: 'Rubric', value: 'Analyst rubric v2.1' },
          { label: 'Reviewer', value: 'H. Kowalski' },
          { label: 'Time on task', value: '74 minutes' },
        ],
        timeline: [
          { label: 'Exercise assigned', time: 'Mon' },
          { label: 'Work sample submitted', time: 'Wed' },
          { label: 'Assigned to reviewer', time: 'Today' },
        ],
        primaryAction: 'Start assessment',
        secondaryAction: 'View rubric',
      },
    },
  },
  {
    slug: 'clinical',
    name: 'Clinical',
    icon: StethoscopeIcon,
    summary: 'Organize documentation, distinguish observations from interpretations, and support professional review.',
    audience: 'For clinical teams, supervisors, and quality reviewers',
    headline: 'Clear documentation. Careful professional review.',
    intro:
      'Clinical teams organize documentation, keep observations distinct from interpretations, and route records to qualified colleagues for review. Noetivra supports professional judgment. It does not diagnose or recommend treatment.',
    tasks: [
      { title: 'Organize documentation', body: 'Keep notes, attachments, and references together in a structured record.' },
      { title: 'Separate observation from interpretation', body: 'Tag what was observed and what was inferred, so reviewers see the difference at a glance.' },
      { title: 'Request professional review', body: 'Route a record to a supervising clinician with the context they need.' },
      { title: 'Maintain an audit trail', body: 'Every edit, comment, and review outcome is recorded and attributable.' },
    ],
    surfaces: [
      {
        name: 'Clinician Workspace',
        audience: 'For practising clinicians',
        body: 'Write and organize documentation, tag observations and interpretations, and request review.',
        items: ['Structured documentation', 'Observation and interpretation tags', 'Linked references', 'Review requests'],
      },
      {
        name: 'Supervision & Review',
        audience: 'For supervisors and quality reviewers',
        body: 'Review documentation, comment on interpretations, and record outcomes for supervision and quality.',
        items: ['Review queue', 'Inline comments', 'Supervision records', 'Audit history'],
      },
    ],
    workflow: {
      title: 'A documentation record, reviewed',
      steps: [
        { title: 'Open a record', body: 'A clinician opens a sample progress note.' },
        { title: 'Tag statements', body: 'Observations and interpretations are marked distinctly.' },
        { title: 'Link references', body: 'Supporting notes and guidelines are attached.' },
        { title: 'Route for review', body: 'A supervising clinician receives the record.' },
        { title: 'Record the outcome', body: 'Comments and sign-off are added to the audit trail.' },
      ],
    },
    modules: ['knowledge', 'capability', 'credentials'],
    demo: {
      url: `${DEMO_BASE}#clinical-chooser`,
      name: 'Noetivra Clinical',
      tryText: 'Tag observations and interpretations in a sample note, then complete a supervisor review.',
    },
    preview: {
      surface: 'Supervision & Review',
      title: 'Documentation review',
      filters: ['All', 'Review requested', 'Signed off'],
      rows: [
        { title: 'Progress note · Sample patient A-17', meta: '6 observations · 2 interpretations', status: 'Review requested', tone: 'warn' },
        { title: 'Intake summary · Sample patient B-04', meta: '9 observations · 1 interpretation', status: 'Commented', tone: 'accent' },
        { title: 'Discharge note · Sample patient C-22', meta: '4 observations', status: 'Signed off', tone: 'ok' },
        { title: 'Case conference · Sample patient A-09', meta: '3 interpretations', status: 'Review requested', tone: 'warn' },
      ],
      detail: {
        title: 'Progress note · Sample patient A-17',
        subtitle: 'Authored by clinician · Sample record',
        fields: [
          { label: 'Observations', value: '6 tagged' },
          { label: 'Interpretations', value: '2 marked for review' },
          { label: 'References', value: '2 linked notes' },
          { label: 'Reviewer', value: 'Supervising clinician' },
        ],
        timeline: [
          { label: 'Note drafted', time: '08:30' },
          { label: 'Statements tagged', time: '08:52' },
          { label: 'Review requested', time: '09:05' },
        ],
        primaryAction: 'Sign off',
        secondaryAction: 'Add comment',
        note: 'Interpretations are flagged for professional review. No automated diagnosis.',
      },
    },
  },
  {
    slug: 'community',
    name: 'Community',
    icon: HeartHandshakeIcon,
    summary: 'Coordinate development opportunities, support requests, and purpose-specific referrals.',
    audience: 'For community organizations, nonprofits, and local networks',
    headline: 'Connect people to the right support, for the right purpose.',
    intro:
      'Members share what they need and discover opportunities to grow. Coordinators match requests with local partners through referrals that state their purpose, and follow up so nobody is lost between services.',
    tasks: [
      { title: 'Coordinate development opportunities', body: 'Offer mentoring, workshops, and programs to the members they suit.' },
      { title: 'Handle support requests', body: 'Receive and prioritize requests from members and partner organizations.' },
      { title: 'Make purpose-specific referrals', body: 'Every referral states why it is made and what information is shared.' },
      { title: 'Follow up with care', body: 'Track outcomes and check in, so support is completed rather than handed off.' },
    ],
    surfaces: [
      {
        name: 'Member Workspace',
        audience: 'For community members',
        body: 'Share a need, explore opportunities, and see the status of your referrals.',
        items: ['Requests and status', 'Development opportunities', 'Referral consent', 'Personal achievements'],
      },
      {
        name: 'Coordinator Workspace',
        audience: 'For coordinators and partner organizations',
        body: 'Review requests, match partners, and follow up across your local network.',
        items: ['Request queue', 'Partner directory', 'Purpose-specific referrals', 'Follow-up tracking'],
      },
    ],
    workflow: {
      title: 'From a shared need to completed support',
      steps: [
        { title: 'Member shares a need', body: 'A new arrival asks for mentoring and language support.' },
        { title: 'Coordinator reviews', body: 'The request is prioritized with relevant context.' },
        { title: 'Referral with a purpose', body: 'A partner receives only what the purpose requires.' },
        { title: 'Partner confirms', body: 'The member sees the confirmed session and contact.' },
        { title: 'Follow-up recorded', body: 'Outcome and next opportunity are logged.' },
      ],
    },
    modules: ['services', 'capability', 'credentials'],
    demo: {
      url: `${DEMO_BASE}#community-chooser`,
      name: 'Noetivra Community',
      tryText: 'Review a sample support request and send a purpose-specific referral to a partner.',
    },
    preview: {
      surface: 'Coordinator Workspace',
      title: 'Support requests',
      filters: ['All', 'Awaiting partner', 'Westside Hub'],
      rows: [
        { title: 'Mentoring for new arrivals', meta: 'Westside Hub · today', status: 'Awaiting partner', tone: 'warn' },
        { title: 'Digital skills workshop', meta: 'Library network · 1 day', status: 'Matched', tone: 'ok' },
        { title: 'Food assistance', meta: 'Harbor Pantry · 2 days', status: 'Referred', tone: 'accent' },
        { title: 'Youth sports program', meta: 'Eastside Center · 5 days', status: 'Follow-up due', tone: 'alert' },
      ],
      detail: {
        title: 'Mentoring for new arrivals',
        subtitle: 'Request CM-208 · Sample record',
        fields: [
          { label: 'Purpose', value: 'Mentoring and language practice' },
          { label: 'Shared with partner', value: 'Name · Language · Availability' },
          { label: 'Partner', value: 'Westside Hub' },
          { label: 'Coordinator', value: 'L. Haddad' },
        ],
        timeline: [
          { label: 'Request shared by member', time: '10:02' },
          { label: 'Consent for referral given', time: '10:05' },
          { label: 'Sent to Westside Hub', time: '10:20' },
        ],
        primaryAction: 'Confirm match',
        secondaryAction: 'Choose another partner',
      },
    },
  },
  {
    slug: 'leadership',
    name: 'Leadership',
    icon: CompassIcon,
    summary:
      'Develop reasoning under pressure, communication clarity, systems thinking, and readiness for responsibility.',
    audience: 'For leadership programs, executive teams, and public-service academies',
    headline: 'Prepare leaders for decisions that carry weight.',
    intro:
      'Participants work through realistic decision scenarios. Facilitators review their reasoning and communication, and each leader receives a development plan built around readiness for greater responsibility.',
    tasks: [
      { title: 'Reasoning under pressure', body: 'Work through time-bound scenarios with incomplete information.' },
      { title: 'Communication clarity', body: 'Brief stakeholders and have clarity reviewed against shared criteria.' },
      { title: 'Systems thinking', body: 'Map second-order effects and trade-offs across connected domains.' },
      { title: 'Readiness for responsibility', body: 'Track development toward roles with greater scope and accountability.' },
    ],
    surfaces: [
      {
        name: 'Participant Workspace',
        audience: 'For leaders in development',
        body: 'Complete scenarios, read facilitator feedback, and follow a personal development plan.',
        items: ['Decision scenarios', 'Reasoning journal', 'Facilitator feedback', 'Readiness plan'],
      },
      {
        name: 'Program Workspace',
        audience: 'For facilitators and program directors',
        body: 'Design scenarios, review reasoning, and follow readiness across a cohort.',
        items: ['Scenario library', 'Reasoning review', 'Cohort readiness', 'Program records'],
      },
    ],
    workflow: {
      title: 'A decision scenario, reviewed',
      steps: [
        { title: 'Enter a scenario', body: 'A sample supply disruption with three stakeholders.' },
        { title: 'Respond under time', body: 'The participant briefs a decision in 20 minutes.' },
        { title: 'Facilitator reviews', body: 'Reasoning and clarity are reviewed with evidence.' },
        { title: 'Development plan', body: 'Focused practice areas are agreed with the leader.' },
        { title: 'Reassess readiness', body: 'A later scenario shows how judgment has developed.' },
      ],
    },
    modules: ['capability', 'credentials', 'knowledge'],
    demo: {
      url: `${DEMO_BASE}#leadership-chooser`,
      name: 'Noetivra Leadership',
      tryText: 'Respond to a sample decision scenario and see how a facilitator reviews your reasoning.',
    },
    preview: {
      surface: 'Program Workspace',
      title: 'Scenario reviews',
      filters: ['Cohort 4', 'Ready for review', 'Systems thinking'],
      rows: [
        { title: 'Supply disruption briefing', meta: 'Cohort 4 · P. Adeyemi', status: 'Ready for review', tone: 'warn' },
        { title: 'Budget trade-off memo', meta: 'Cohort 4 · E. Lindgren', status: 'In review', tone: 'accent' },
        { title: 'Crisis communication drill', meta: 'Cohort 4 · S. Navarro', status: 'Plan agreed', tone: 'ok' },
        { title: 'Stakeholder alignment case', meta: 'Cohort 3 · M. Tanaka', status: 'Reassess due', tone: 'alert' },
      ],
      detail: {
        title: 'Supply disruption briefing',
        subtitle: 'P. Adeyemi · Sample record',
        fields: [
          { label: 'Scenario', value: 'Regional supply disruption' },
          { label: 'Time limit', value: '20 minutes' },
          { label: 'Focus', value: 'Reasoning · Clarity · Systems' },
          { label: 'Facilitator', value: 'Dr. N. Osei' },
        ],
        timeline: [
          { label: 'Scenario started', time: '14:00' },
          { label: 'Decision brief submitted', time: '14:19' },
          { label: 'Queued for facilitator', time: '14:20' },
        ],
        primaryAction: 'Review reasoning',
        secondaryAction: 'View scenario',
      },
    },
  },
];
